import Page from '@app/components/layout/page/page';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetSecrConfig,
  useUpdateSecrConfig,
} from '@shared/api/hooks/ccf/secr';
import Alert from '@shared/components/alert';
import Heading from '@shared/components/content/heading';
import Text from '@shared/components/content/text';
import SelectFormField from '@shared/components/form/select-form-field';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { Skeleton } from '@shared/components/ui/skeleton';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate } from 'react-router-dom';
import { ccfRoutes } from '../../ccf-routes';
import { useCCF } from '../ccf-context';
import { ActionsTakenEditor } from './components/secr-actions-taken';
import { SECRTableView } from './components/secr-emissions-table';
import { IntensityRatioTable } from './components/secr-intensity-table';
import { MethodologySection } from './components/secr-methodology';
import { PREVIOUS_SOURCE_OPTIONS } from './constants';
import {
  DEFAULT_VALUES,
  secrFormSchema,
  type CurrentYearData,
  type EmissionRowKey,
  type SecrFormValues,
} from './schema';
import { mapConfigToFormValues, mapFormValuesToPayload } from './utils';

export default function SecrConfigurationPage() {
  const {
    uuid: reportUuid,
    startDate,
    endDate,
    status: reportStatus,
    firstPublishedAt,
  } = useCCF();
  const { toast } = useToast();
  const navigate = useNavigate();

  const reportYear = parseInt(startDate.slice(0, 4), 10);
  const prevYearLabel = `${reportYear - 1}/${String(reportYear).slice(2)}`;
  const currentYearLabel = `${reportYear}/${String(reportYear + 1).slice(2)}`;

  const { data: secrConfig, isLoading: configLoading } =
    useGetSecrConfig(reportUuid);
  const { mutateAsync: updateSecrConfig, isLoading: submitting } =
    useUpdateSecrConfig();

  const currentYearData = React.useMemo<CurrentYearData | undefined>(() => {
    const cy = secrConfig?.currentYear;
    if (!cy) return undefined;
    const get = (key: EmissionRowKey) => {
      const cat = cy.categories[key];
      return { tco2e: cat?.tonnes ?? null, kwh: cat?.kwh ?? null };
    };
    return {
      stationaryCombustion: get('stationaryCombustion'),
      mobileCombustion: get('mobileCombustion'),
      electricity: get('electricity'),
      heat: get('heat'),
      businessTravel: get('businessTravel'),
    };
  }, [secrConfig?.currentYear]);

  const currentTotalTco2e = secrConfig?.currentYear?.totals.tonnes ?? 0;

  const form = useForm<SecrFormValues>({
    resolver: zodResolver(secrFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { watch, handleSubmit, reset } = form;
  const previousEmissionsSource = watch('previousEmissionsSource');

  useEffect(() => {
    if (!secrConfig) return;
    const mapped = mapConfigToFormValues(secrConfig);
    reset({ ...DEFAULT_VALUES, ...mapped });
  }, [secrConfig, reset]);

  const hasPriorYear = secrConfig?.priorYearComputed != null;

  const handleCancel = () => {
    navigate(
      generatePath(ccfRoutes.COMPANY_REPORT, { report_uuid: reportUuid })
    );
  };

  const onSubmit = async (data: SecrFormValues) => {
    try {
      await updateSecrConfig({
        companyReportUuid: reportUuid,
        data: mapFormValuesToPayload(data),
      });
      toast({ title: 'SECR configuration saved', variant: 'success' });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to save SECR configuration',
        description:
          'Please try again or contact support if the issue persists.',
        variant: 'destructive',
      });
    }
  };

  if (configLoading) {
    return (
      <Page name="SECR configuration" page="subpage">
        <div className="max-w-4xl space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Page>
    );
  }

  const neverPublished = !firstPublishedAt;
  const missingCurrentYearValues =
    !neverPublished &&
    (reportStatus === 'edited' || reportStatus === 'unpublished') &&
    secrConfig?.currentYear == null;

  return (
    <Page name="SECR configuration" page="subpage">
      {neverPublished && (
        <Alert variant="warning" className="mb-6 max-w-4xl">
          You need to publish your company report before you can see your
          current year values.
        </Alert>
      )}
      {missingCurrentYearValues && (
        <Alert variant="warning" className="mb-6 max-w-4xl">
          Your current year values haven&apos;t been calculated yet. Republish
          your company report to generate the SECR figures.
        </Alert>
      )}
      {!neverPublished &&
        !missingCurrentYearValues &&
        reportStatus === 'edited' && (
          <Alert variant="info" className="mb-6 max-w-4xl">
            You have unpublished changes in your company report. Republish your
            report to see them reflected in the SECR.
          </Alert>
        )}
      <Form {...form}>
        <form
          className="min-h-[360px] max-w-4xl space-y-6"
          onSubmit={handleSubmit(onSubmit)}
        >
          <fieldset className="space-y-6" disabled={submitting}>
            {/* Previous data source */}
            <div className="space-y-3">
              <div>
                <Heading variant="section">Previous year data</Heading>
                <Text variant="subtle" className="mt-1">
                  If you have completed a SECR report for the previous year, you
                  must include the figures in this year&apos;s report.
                </Text>
              </div>
              <SelectFormField
                name="previousEmissionsSource"
                label={`Have you completed a SECR report for ${prevYearLabel}?`}
                className="w-96"
                options={PREVIOUS_SOURCE_OPTIONS.map((opt) =>
                  opt.value === 'my_emissions'
                    ? { ...opt, disabled: !secrConfig?.hasPriorYearResult }
                    : opt
                )}
              />
            </div>

            {/* Emissions & energy */}
            <div className="space-y-3">
              <div>
                <Heading variant="section">Emissions &amp; energy</Heading>
                <Text variant="subtle" className="mt-1">
                  {previousEmissionsSource === 'off_platform' ? (
                    <>
                      The table below shows the calculated total for each
                      category, based on the activities in your{' '}
                      {currentYearLabel} company report. You need to enter the
                      totals from your {prevYearLabel} SECR report.
                    </>
                  ) : previousEmissionsSource === 'my_emissions' ? (
                    <>
                      The table below shows the calculated total for each
                      category, based on the activities in your company reports.
                      You do not need to enter any data.{' '}
                      <span className="font-semibold">
                        Only published prior-year report results are shown.
                      </span>
                    </>
                  ) : (
                    <>
                      The table below shows the calculated totals for each
                      category, based on the activities in your company report.
                      You do not need to enter any data.
                    </>
                  )}
                </Text>
              </div>
              <SECRTableView
                source={previousEmissionsSource}
                prevYearLabel={prevYearLabel}
                currentYearLabel={currentYearLabel}
                currentYearData={currentYearData}
                priorYearComputed={secrConfig?.priorYearComputed}
              />
            </div>

            {/* Emissions intensity */}
            <div className="space-y-3">
              <div>
                <Heading variant="section">Emissions intensity ratio</Heading>
                <Text variant="subtle" className="mt-1">
                  An intensity ratio contextualises your total emissions using a
                  second business metric, such as revenue, and allows for easy
                  comparison between years. You need to select which metric to
                  use and then enter the value of that metric for{' '}
                  {previousEmissionsSource === 'none'
                    ? 'this reporting year.'
                    : 'each reporting year.'}
                </Text>
              </div>
              <IntensityRatioTable
                source={previousEmissionsSource}
                currentTotalTco2e={currentTotalTco2e}
                priorYearComputed={secrConfig?.priorYearComputed}
              />
            </div>

            {/* Methodology */}
            <MethodologySection hasPriorYear={hasPriorYear} />

            {/* Actions taken */}
            <div className="space-y-3">
              <div>
                <Heading variant="section">Actions taken</Heading>
                <Text variant="subtle" className="mt-1">
                  Describe the energy efficiency actions and measures taken
                  during this reporting period.
                </Text>
              </div>
              <ActionsTakenEditor />
            </div>
          </fieldset>

          <div className="flex justify-between gap-2 pb-8 pt-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save configuration'}
            </Button>
            <Button
              type="button"
              variant="outline-destructive"
              onClick={handleCancel}
            >
              Discard changes
            </Button>
          </div>
        </form>
      </Form>
    </Page>
  );
}
